// Auth helpers for the B2B console.
//
// Admin signs in with the real Django superuser credentials against the
// existing backend endpoints:
//   POST /API/v2/superuser/login/    { username, password } -> { access_token, refresh_token }
//   POST /API/v2/superuser/refresh/  { refresh_token }       -> { access_token }
//
// HR and Employee logins are not wired yet (see Login page).
import { API_BASE_URL } from './config';

const ACCESS_KEY = 'access_token';
const REFRESH_KEY = 'refresh_token';
const ROLE_KEY = 'auth_role';
const HR_EMAIL_KEY = 'hr_email';
const HR_COMPANY_KEY = 'hr_company';
const HR_MUST_CHANGE_KEY = 'hr_must_change';

export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY);
}

export function getRole() {
  return localStorage.getItem(ROLE_KEY);
}

export function isAuthenticated() {
  return !!getAccessToken();
}

// Role-aware gate: is the current session authenticated for this dashboard?
// Only 'admin' and 'hr' use this console — employees use the mobile app.
export function isAuthenticatedFor(role) {
  return !!getAccessToken() && getRole() === role;
}

export function storeSession({ access_token, refresh_token, role }) {
  if (access_token) localStorage.setItem(ACCESS_KEY, access_token);
  if (refresh_token) localStorage.setItem(REFRESH_KEY, refresh_token);
  if (role) localStorage.setItem(ROLE_KEY, role);
}

export function clearSession() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(ROLE_KEY);
  localStorage.removeItem(HR_EMAIL_KEY);
  localStorage.removeItem(HR_COMPANY_KEY);
  localStorage.removeItem(HR_MUST_CHANGE_KEY);
}

// ---- HR (CompanyAdmin) context ----
export function getHrEmail() {
  return localStorage.getItem(HR_EMAIL_KEY) || '';
}

export function getHrCompany() {
  try {
    return JSON.parse(localStorage.getItem(HR_COMPANY_KEY) || 'null');
  } catch (_) {
    return null;
  }
}

export function hrMustChangePassword() {
  return localStorage.getItem(HR_MUST_CHANGE_KEY) === '1';
}

function storeHrContext(data) {
  if (data.hr_email) localStorage.setItem(HR_EMAIL_KEY, data.hr_email);
  if (data.company) localStorage.setItem(HR_COMPANY_KEY, JSON.stringify(data.company));
  localStorage.setItem(HR_MUST_CHANGE_KEY, data.must_change_password ? '1' : '0');
}

// Update the cached company logo (HR's DP) after HR changes it.
export function setHrCompanyLogo(logoUrl) {
  const company = getHrCompany() || {};
  company.logo_url = logoUrl;
  localStorage.setItem(HR_COMPANY_KEY, JSON.stringify(company));
}

export async function adminLogin(username, password) {
  const res = await fetch(`${API_BASE_URL}/API/v2/superuser/login/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || 'Login failed');
  }
  storeSession({
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    role: 'admin',
  });
  return data;
}

// Exchange the stored refresh token for a fresh access token.
export async function adminRefresh() {
  const refresh_token = getRefreshToken();
  if (!refresh_token) throw new Error('No refresh token');
  const res = await fetch(`${API_BASE_URL}/API/v2/superuser/refresh/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Refresh failed');
  if (data.access_token) localStorage.setItem(ACCESS_KEY, data.access_token);
  return data.access_token;
}

// ---- HR auth ----
export async function hrLogin(email, password) {
  const res = await fetch(`${API_BASE_URL}/API/v2/b2b/hr/login/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.error || 'Login failed');
  }
  storeSession({
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    role: 'hr',
  });
  storeHrContext(data);
  return data;
}

export async function hrRefresh() {
  const refresh_token = getRefreshToken();
  if (!refresh_token) throw new Error('No refresh token');
  const res = await fetch(`${API_BASE_URL}/API/v2/b2b/hr/refresh/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.error || 'Refresh failed');
  if (data.access_token) localStorage.setItem(ACCESS_KEY, data.access_token);
  return data.access_token;
}

export async function hrChangePassword(currentPassword, newPassword) {
  const res = await fetch(`${API_BASE_URL}/API/v2/b2b/hr/change-password/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAccessToken()}`,
    },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.error || 'Change failed');
  localStorage.setItem(HR_MUST_CHANGE_KEY, '0');
  return data;
}

// Refresh the current session's access token using the role-appropriate endpoint.
export async function refreshSession() {
  return getRole() === 'hr' ? hrRefresh() : adminRefresh();
}
